import React, { useEffect, Children, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, XCircleIcon, CheckCircleIcon, SearchIcon, MapIcon, GitBranchIcon, SettingsIcon, ZapIcon, RefreshCwIcon, HelpCircleIcon, WalletIcon, ShieldIcon, TargetIcon, RocketIcon, BookOpenIcon, TrendingUpIcon, CompassIcon, ShieldCheckIcon, FootprintsIcon, PlayCircleIcon } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function FirstHomeBuyers() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <main className="pt-20 pb-0 bg-[#f8f9fb] dark:bg-[#0f1629] transition-colors duration-300 overflow-hidden" data-id="element-1426">
      {/* SECTION 1 — HERO */}
      <section className="relative pt-24 pb-32 overflow-hidden" data-id="element-1427">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-primary-500/10 dark:bg-primary-500/5 rounded-full blur-[120px] -z-10" data-id="element-1428"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-accent-500/10 dark:bg-accent-500/5 rounded-full blur-[120px] -z-10" data-id="element-1429"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1430">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1431">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} data-id="element-1432">
              <motion.div variants={fadeIn} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none mb-8" data-id="element-1433">
                <span className="flex h-2 w-2 rounded-full bg-primary-500 animate-pulse" data-id="element-1434"></span>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300" data-id="element-1435">
                  First Home Buyer Accelerator
                </span>
              </motion.div>

              <motion.h1 variants={fadeIn} className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-6 leading-[1.1]" data-id="element-1436">
                The First Home Buyer <br data-id="element-1437" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-primary-500" data-id="element-1438">
                  Accelerator System
                </span>
              </motion.h1>

              <motion.p variants={fadeIn} className="text-xl md:text-2xl text-slate-800 dark:text-slate-200 font-medium mb-4" data-id="element-1439">
                Don't just buy your first home. Build your first move properly.
              </motion.p>

              <motion.p variants={fadeIn} className="text-lg text-slate-600 dark:text-slate-400 mb-10 leading-relaxed max-w-xl" data-id="element-1440">
                A strategic system for Australian first home buyers who want
                clarity, confidence, and a smarter path into property.
              </motion.p>

              <motion.div variants={fadeIn} className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1441">
                <Link to="/first-home-buyers/clarity" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold text-lg hover:shadow-[0_0_30px_rgba(20,184,166,0.35)] transition-all flex items-center justify-center gap-2 group hover:scale-[1.02]" data-id="element-1442">
                  See My Best Next Move
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1443" />
                </Link>
                <button onClick={() => scrollToSection('how-it-works')} className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white dark:bg-white/5 hover:bg-slate-50 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white font-semibold text-lg shadow-sm dark:shadow-none transition-all flex items-center justify-center gap-2" data-id="element-1444">
                  <PlayCircleIcon className="w-5 h-5 text-primary-500" data-id="element-1445" />
                  Watch How It Works
                </button>
              </motion.div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative hidden lg:block" data-id="element-1446">
              <div className="bg-white dark:bg-[#1a2035] border border-slate-200 dark:border-white/10 rounded-3xl p-8 shadow-xl relative overflow-hidden" data-id="element-1447">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary-500/20 to-transparent rounded-bl-full" data-id="element-1448"></div>

                <h3 className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-8" data-id="element-1449">
                  The Smarter Pathway
                </h3>

                <div className="space-y-6 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-slate-200 before:to-primary-500 dark:before:from-white/10 dark:before:to-primary-500" data-id="element-1450">
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-1451">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-white dark:border-[#1a2035] bg-slate-100 dark:bg-slate-800 text-slate-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-1452">
                      <span className="text-sm font-bold" data-id="element-1453">01</span>
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] p-4 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5 shadow-sm" data-id="element-1454">
                      <h4 className="font-semibold text-slate-900 dark:text-white text-sm" data-id="element-1455">
                        Renting & Saving
                      </h4>
                      <p className="text-xs text-slate-500 mt-1" data-id="element-1456">
                        Gaining clarity on position
                      </p>
                    </div>
                  </div>

                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-1457">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-white dark:border-[#1a2035] bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-1458">
                      <span className="text-sm font-bold" data-id="element-1459">02</span>
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] p-4 rounded-2xl bg-primary-50 dark:bg-primary-500/10 border border-primary-100 dark:border-primary-500/20 shadow-sm" data-id="element-1460">
                      <h4 className="font-semibold text-primary-900 dark:text-primary-100 text-sm" data-id="element-1461">
                        First Property
                      </h4>
                      <p className="text-xs text-primary-700 dark:text-primary-300 mt-1" data-id="element-1462">
                        Structured for growth
                      </p>
                    </div>
                  </div>

                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-1463">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-white dark:border-[#1a2035] bg-accent-100 dark:bg-accent-500/20 text-accent-600 dark:text-accent-400 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-1464">
                      <span className="text-sm font-bold" data-id="element-1465">03</span>
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] p-4 rounded-2xl bg-accent-50 dark:bg-accent-500/10 border border-accent-100 dark:border-accent-500/20 shadow-sm" data-id="element-1466">
                      <h4 className="font-semibold text-accent-900 dark:text-accent-100 text-sm" data-id="element-1467">
                        Building Equity
                      </h4>
                      <p className="text-xs text-accent-700 dark:text-accent-300 mt-1" data-id="element-1468">
                        Manufacturing value
                      </p>
                    </div>
                  </div>

                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-1469">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-white dark:border-[#1a2035] bg-gradient-to-r from-accent-500 to-primary-500 text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-1470">
                      <TargetIcon className="w-5 h-5" data-id="element-1471" />
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] p-4 rounded-2xl bg-gradient-to-r from-slate-900 to-slate-800 text-white shadow-lg" data-id="element-1472">
                      <h4 className="font-semibold text-white text-sm" data-id="element-1473">
                        Next Move
                      </h4>
                      <p className="text-xs text-slate-300 mt-1" data-id="element-1474">
                        Scaling the portfolio
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — CONTRARIAN REFRAME */}
      <section className="py-24 bg-white dark:bg-[#1a2035] border-y border-slate-100 dark:border-white/5" data-id="element-1475">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1476">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="text-center mb-16" data-id="element-1477">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-1478">
              Why Most First Home Buyers Stay Stuck
            </h2>
            <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto" data-id="element-1479">
              The traditional advice is keeping you out of the market.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-id="element-1480">
            {['I need 20% deposit', 'My first property has to be my dream home', 'I just need to save harder', "If I get approved, I'm ready", 'The cheapest loan is all that matters', 'Government schemes will solve everything'].map((myth, i) => <motion.div key={i} variants={fadeIn} className="flex items-start gap-4 p-6 rounded-2xl bg-red-50/50 dark:bg-red-500/5 border border-red-100 dark:border-red-500/10" data-id="element-1481">
                <XCircleIcon className="w-6 h-6 text-red-500 shrink-0 mt-0.5" data-id="element-1482" />
                <p className="text-slate-700 dark:text-slate-300 font-medium" data-id="element-1483">
                  {myth}
                </p>
              </motion.div>)}
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="max-w-3xl mx-auto text-center p-8 md:p-10 rounded-3xl bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10" data-id="element-1484">
            <p className="text-xl md:text-2xl font-semibold text-slate-900 dark:text-white leading-relaxed" data-id="element-1485">
              Most first home buyers don't fail because they're lazy.{' '}
              <br className="hidden md:block" data-id="element-1486" />
              <span className="text-primary-600 dark:text-primary-400" data-id="element-1487">
                They fail because nobody shows them the right strategy.
              </span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 3 — PROGRAM INTRO */}
      <section className="py-24" data-id="element-1488">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1489">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1490">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} data-id="element-1491">
              <motion.h2 variants={fadeIn} className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6 leading-tight" data-id="element-1492">
                This Isn't Just Mortgage Broking. <br data-id="element-1493" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-primary-500" data-id="element-1494">
                  It's a Strategy System.
                </span>
              </motion.h2>

              <motion.p variants={fadeIn} className="text-lg text-slate-600 dark:text-slate-400 mb-8 leading-relaxed" data-id="element-1495">
                The First Home Buyer Accelerator is designed to help you bypass
                the confusion, map out your exact options, and execute a
                purchase that sets you up for the future.
              </motion.p>

              <motion.ul variants={staggerContainer} className="space-y-4" data-id="element-1496">
                {['Understand what you can actually do', 'Choose the right buying path', 'Avoid bad first purchases', 'Structure the loan properly', "Think beyond just 'getting approved'"].map((point, i) => <motion.li key={i} variants={fadeIn} className="flex items-start gap-3" data-id="element-1497">
                    <CheckCircleIcon className="w-6 h-6 text-primary-500 shrink-0" data-id="element-1498" />
                    <span className="text-slate-700 dark:text-slate-300 font-medium text-lg" data-id="element-1499">
                      {point}
                    </span>
                  </motion.li>)}
              </motion.ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-white dark:bg-[#1a2035] p-8 rounded-3xl border border-slate-200 dark:border-white/10 shadow-xl" data-id="element-1500">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2" data-id="element-1501">
                <SettingsIcon className="w-6 h-6 text-primary-500" data-id="element-1502" />
                The Accelerator Framework
              </h3>

              <div className="space-y-6" data-id="element-1503">
                {[{
                step: '01',
                title: 'Diagnosis',
                desc: 'Assess borrowing power, deposit, and goals.'
              }, {
                step: '02',
                title: 'Pathway Mapping',
                desc: 'Compare PPOR vs Rentvesting vs Schemes.'
              }, {
                step: '03',
                title: 'Structuring',
                desc: 'Set up finance for maximum future flexibility.'
              }, {
                step: '04',
                title: 'Execution',
                desc: 'Guided support through to settlement.'
              }].map((item, i) => <div key={i} className="flex gap-4 p-4 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5" data-id="element-1504">
                    <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300 font-bold flex items-center justify-center shrink-0" data-id="element-1505">
                      {item.step}
                    </div>
                    <div data-id="element-1506">
                      <h4 className="font-semibold text-slate-900 dark:text-white" data-id="element-1507">
                        {item.title}
                      </h4>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1" data-id="element-1508">
                        {item.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — VALUE STACK / PROGRAM MODULES */}
      <section className="py-24 bg-white dark:bg-[#1a2035] border-y border-slate-100 dark:border-white/5" data-id="element-1509">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1510">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="text-center mb-16" data-id="element-1511">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-1512">
              What's Inside the Accelerator
            </h2>
            <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto" data-id="element-1513">
              Everything you need to make a strategic first move into the
              property market.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1514">
            {[{
            title: 'FHB Clarity Tool',
            desc: 'Find out what you may be able to afford, possible buying pathways, and your next best move.',
            icon: SearchIcon
          }, {
            title: 'Strategy Blueprint',
            desc: 'A personalised game plan based on deposit, borrowing power, goals, and timeline.',
            icon: MapIcon
          }, {
            title: 'Buying Pathway Mapping',
            desc: 'Compare buying to live in, rentvesting, using family support, low-deposit options, and scheme pathways.',
            icon: GitBranchIcon
          }, {
            title: 'Loan Structuring Guidance',
            desc: "Set up the finance properly so your first purchase doesn't hold you back later.",
            icon: SettingsIcon
          }, {
            title: 'Fast-Track Approval Support',
            desc: 'Simple step-by-step support from planning to approval and settlement.',
            icon: ZapIcon
          }, {
            title: 'Post-Purchase Review',
            desc: 'A future-focused review so the first purchase becomes a stepping stone, not a dead end.',
            icon: RefreshCwIcon
          }].map((module, i) => {
            const Icon = module.icon;
            return <motion.div key={i} variants={fadeIn} className="relative p-8 rounded-3xl bg-white dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-md transition-shadow" data-id="element-1515">
                  <div className="absolute top-6 right-6 text-4xl font-black text-slate-100 dark:text-white/5 select-none" data-id="element-1516">
                    0{i + 1}
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center mb-6 relative z-10" data-id="element-1517">
                    <Icon className="w-7 h-7 text-primary-600 dark:text-primary-400" data-id="element-1518" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 relative z-10" data-id="element-1519">
                    {module.title}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 leading-relaxed relative z-10" data-id="element-1520">
                    {module.desc}
                  </p>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* SECTION 5 — CASE STUDY */}
      <section className="py-24" data-id="element-1521">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1522">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="text-center mb-16" data-id="element-1523">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-1524">
              What This Looks Like in Real Life
            </h2>
            <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto" data-id="element-1525">
              A smarter approach changes the timeline entirely.
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto" data-id="element-1526">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-1527">
              <motion.div variants={fadeIn} className="p-8 rounded-3xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10" data-id="element-1528">
                <div className="inline-block px-3 py-1 rounded-full bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-300 text-xs font-bold uppercase tracking-wider mb-4" data-id="element-1529">
                  Before
                </div>
                <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed" data-id="element-1530">
                  Sarah & James thought they needed 3 more years to save for a
                  20% deposit on their dream home in the suburbs.
                </p>
              </motion.div>

              <motion.div variants={fadeIn} className="p-8 rounded-3xl bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/20" data-id="element-1531">
                <div className="inline-block px-3 py-1 rounded-full bg-primary-200 dark:bg-primary-500/30 text-primary-800 dark:text-primary-200 text-xs font-bold uppercase tracking-wider mb-4" data-id="element-1532">
                  After
                </div>
                <p className="text-lg text-primary-900 dark:text-primary-100 leading-relaxed font-medium" data-id="element-1533">
                  Within 8 weeks, they purchased their first property using a
                  smarter strategy — and they're already positioned for their
                  next move.
                </p>
              </motion.div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} className="relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-slate-200 dark:before:bg-white/10" data-id="element-1534">
              {[{
              title: 'Their Situation',
              desc: 'Renting, $45k saved, unsure of options'
            }, {
              title: 'What They Believed',
              desc: 'Need $120k deposit, must buy dream home first'
            }, {
              title: 'The Strategy',
              desc: 'Rentvest with 5% deposit, structure for growth'
            }, {
              title: 'The Result',
              desc: 'Purchased at $520k, equity building, next move mapped'
            }].map((step, i) => <motion.div key={i} variants={fadeIn} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active mb-8 last:mb-0" data-id="element-1535">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-[#f8f9fb] dark:border-[#0f1629] bg-primary-500 text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-1536">
                    <span className="text-sm font-bold" data-id="element-1537">{i + 1}</span>
                  </div>
                  <div className="w-[calc(100%-3rem)] md:w-[calc(50%-2.5rem)] p-6 rounded-2xl bg-white dark:bg-[#1a2035] border border-slate-200 dark:border-white/10 shadow-sm" data-id="element-1538">
                    <h4 className="font-bold text-slate-900 dark:text-white text-lg mb-1" data-id="element-1539">
                      {step.title}
                    </h4>
                    <p className="text-slate-600 dark:text-slate-400" data-id="element-1540">
                      {step.desc}
                    </p>
                  </div>
                </motion.div>)}
            </motion.div>

            <p className="text-center text-xs text-slate-400 mt-12 italic" data-id="element-1541">
              This is a hypothetical example for illustrative purposes only.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 6 — SELF-SELECTION / DIAGNOSIS */}
      <section className="py-24 bg-white dark:bg-[#1a2035] border-y border-slate-100 dark:border-white/5" data-id="element-1542">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1543">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="text-center mb-16" data-id="element-1544">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-1545">
              Which Situation Sounds Most Like You?
            </h2>
            <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto" data-id="element-1546">
              Select your current position to see how we can help.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-1547">
            {[{
            text: "I don't know if I can buy yet",
            icon: HelpCircleIcon
          }, {
            text: "I have some savings but don't know my best option",
            icon: WalletIcon
          }, {
            text: "I could buy, but I don't want to make the wrong move",
            icon: ShieldIcon
          }, {
            text: 'I want a smarter first-home strategy, not just a loan',
            icon: TargetIcon
          }].map((card, i) => {
            const Icon = card.icon;
            return <motion.div key={i} variants={fadeIn} data-id="element-1548">
                  <Link to="/#contact" className="group flex items-center justify-between p-6 rounded-2xl bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 hover:border-primary-500 dark:hover:border-primary-500 hover:shadow-md transition-all" data-id="element-1549">
                    <div className="flex items-center gap-4" data-id="element-1550">
                      <div className="w-12 h-12 rounded-full bg-white dark:bg-[#1a2035] flex items-center justify-center shadow-sm group-hover:bg-primary-50 dark:group-hover:bg-primary-500/10 transition-colors" data-id="element-1551">
                        <Icon className="w-6 h-6 text-slate-600 dark:text-slate-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" data-id="element-1552" />
                      </div>
                      <span className="font-semibold text-slate-800 dark:text-slate-200 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" data-id="element-1553">
                        {card.text}
                      </span>
                    </div>
                    <ArrowRightIcon className="w-5 h-5 text-slate-400 group-hover:text-primary-500 group-hover:translate-x-1 transition-all" data-id="element-1554" />
                  </Link>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* SECTION 7 — MYTH-BREAKER STRIP */}
      <section className="py-16 bg-slate-900 dark:bg-[#0a0f1c]" data-id="element-1555">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1556">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="flex flex-col lg:flex-row gap-6" data-id="element-1557">
            {[{
            myth: 'You need 20% deposit',
            truth: 'Sometimes waiting for 20% can cost more than moving sooner.'
          }, {
            myth: 'Your first property should be your forever home',
            truth: 'Your first property should put you in a stronger position.'
          }, {
            myth: "Approval means you're ready",
            truth: 'Strategy matters before finance.'
          }].map((item, i) => <motion.div key={i} variants={fadeIn} className="flex-1 rounded-2xl overflow-hidden flex flex-col sm:flex-row lg:flex-col" data-id="element-1558">
                <div className="bg-red-500/10 border border-red-500/20 p-5 flex-1" data-id="element-1559">
                  <p className="text-red-400 text-xs font-bold uppercase tracking-wider mb-2" data-id="element-1560">
                    Myth
                  </p>
                  <p className="text-white font-medium" data-id="element-1561">{item.myth}</p>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/20 p-5 flex-1" data-id="element-1562">
                  <p className="text-emerald-400 text-xs font-bold uppercase tracking-wider mb-2" data-id="element-1563">
                    Truth
                  </p>
                  <p className="text-white font-medium" data-id="element-1564">{item.truth}</p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* SECTION 8 — 3-STEP PROCESS */}
      <section id="how-it-works" className="py-24 bg-white dark:bg-[#1a2035] border-y border-slate-100 dark:border-white/5" data-id="element-1565">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1566">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="text-center mb-16" data-id="element-1567">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-1568">
              A Smarter Way to Buy Your First Property
            </h2>
            <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto" data-id="element-1569">
              Three simple steps to execute your first move with confidence.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto relative" data-id="element-1570">
            <div className="hidden md:block absolute top-12 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-transparent via-slate-200 dark:via-white/10 to-transparent" data-id="element-1571"></div>

            {[{
            title: 'Get Clarity',
            desc: 'Use the tool and understand your real position.',
            icon: SearchIcon
          }, {
            title: 'Build the Right Strategy',
            desc: 'Map the most suitable buying path for your situation.',
            icon: MapIcon
          }, {
            title: 'Execute With Confidence',
            desc: 'Get guided through finance, approval, and settlement.',
            icon: RocketIcon
          }].map((step, i) => {
            const Icon = step.icon;
            return <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeIn} className="relative flex flex-col items-center text-center" data-id="element-1572">
                  <div className="relative mb-6" data-id="element-1573">
                    <div className="w-24 h-24 rounded-3xl bg-slate-50 dark:bg-[#0f1629] border-2 border-slate-200 dark:border-white/10 flex items-center justify-center shadow-sm relative z-10" data-id="element-1574">
                      <Icon className="w-10 h-10 text-primary-600 dark:text-primary-400" data-id="element-1575" />
                    </div>
                    <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-gradient-to-br from-accent-500 to-primary-500 text-white text-sm font-bold flex items-center justify-center shadow-md z-20" data-id="element-1576">
                      {i + 1}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3" data-id="element-1577">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed" data-id="element-1578">
                    {step.desc}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* SECTION 9 — CTA / TOOL SECTION */}
      <section className="py-24" data-id="element-1579">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1580">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="p-10 md:p-16 rounded-[2.5rem] bg-gradient-to-br from-slate-900 to-slate-800 dark:from-[#1a2035] dark:to-[#0f1629] border border-slate-800 dark:border-white/10 shadow-2xl text-center relative overflow-hidden" data-id="element-1581">
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/20 rounded-full blur-[100px]" data-id="element-1582"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-500/20 rounded-full blur-[100px]" data-id="element-1583"></div>

            <div className="relative z-10" data-id="element-1584">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1585">
                Find Your Best Next Move
              </h2>
              <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto" data-id="element-1586">
                Use the First Home Buyer Clarity Tool to see what your path
                could look like.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1587">
                <Link to="/first-home-buyers/clarity" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-bold text-lg hover:shadow-[0_0_30px_rgba(20,184,166,0.4)] transition-all flex items-center justify-center gap-2 hover:scale-[1.02]" data-id="element-1588">
                  Start My Free Strategy Tool
                  <ArrowRightIcon className="w-5 h-5" data-id="element-1589" />
                </Link>
                <Link to="/#contact" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold text-lg transition-all flex items-center justify-center" data-id="element-1590">
                  Book a Strategy Session
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 10 — TRUST / POSITIONING */}
      <section className="py-24 bg-white dark:bg-[#1a2035] border-y border-slate-100 dark:border-white/5" data-id="element-1591">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1592">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="text-center mb-16" data-id="element-1593">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-1594">
              Built for First Home Buyers Who Want to Do This Properly
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-center" data-id="element-1595">
            {[{
            text: 'Education-first approach',
            icon: BookOpenIcon
          }, {
            text: 'Long-term strategy mindset',
            icon: TrendingUpIcon
          }, {
            text: 'Clear, practical guidance',
            icon: CompassIcon
          }, {
            text: 'Designed to reduce confusion and bad decisions',
            icon: ShieldCheckIcon
          }, {
            text: 'Focused on building the right first step',
            icon: FootprintsIcon
          }].map((point, i) => {
            const Icon = point.icon;
            return <motion.div key={i} variants={fadeIn} className="flex items-center gap-4 p-6 rounded-2xl bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10" data-id="element-1596">
                  <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-500/20 flex items-center justify-center shrink-0" data-id="element-1597">
                    <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" data-id="element-1598" />
                  </div>
                  <span className="font-semibold text-slate-800 dark:text-slate-200" data-id="element-1599">
                    {point.text}
                  </span>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* SECTION 11 — FINAL CTA */}
      <section className="py-32 relative overflow-hidden" data-id="element-1600">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary-50/50 dark:to-primary-900/10 -z-10" data-id="element-1601"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1602">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-1603">
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-6 leading-tight" data-id="element-1604">
              Your First Property Should Move You Forward —{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-primary-500" data-id="element-1605">
                Not Hold You Back
              </span>
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-400 mb-10" data-id="element-1606">
              Get clarity on your options and a smarter starting point.
            </p>
            <Link to="/first-home-buyers/clarity" className="inline-flex items-center gap-2 px-10 py-5 rounded-2xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-bold text-xl hover:shadow-[0_0_40px_rgba(20,184,166,0.4)] transition-all hover:scale-[1.02]" data-id="element-1607">
              See My Best Next Move
              <ArrowRightIcon className="w-6 h-6" data-id="element-1608" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* SECTION 12 — FOOTER DISCLAIMER */}
      <div className="bg-slate-100 dark:bg-[#0a0f1c] py-6 border-t border-slate-200 dark:border-white/5" data-id="element-1609">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1610">
          <p className="text-xs text-slate-500 dark:text-slate-500 max-w-4xl mx-auto leading-relaxed" data-id="element-1611">
            General advice only. This information does not consider your
            personal financial situation, objectives or needs. Any tools,
            examples or calculators are indicative only and may not be accurate.
          </p>
        </div>
      </div>
    </main>;
}